import { useState, useEffect } from 'preact/hooks'
import { formatTorontoTime, getTorontoTimezone } from '../utils/torontoTime'

export function MobileEnhancements({ effectiveTime, isSimulating, currentSessions, upcomingSessions }) {
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [showQuickActions, setShowQuickActions] = useState(false)
  const [orientation, setOrientation] = useState('portrait')

  useEffect(() => {
    const handleOrientationChange = () => {
      setOrientation(window.innerHeight > window.innerWidth ? 'portrait' : 'landscape')
    }
    
    handleOrientationChange()
    window.addEventListener('resize', handleOrientationChange)
    return () => window.removeEventListener('resize', handleOrientationChange)
  }, [])

  const enterFullscreen = () => {
    if (document.documentElement.requestFullscreen) {
      document.documentElement.requestFullscreen()
      setIsFullscreen(true)
    }
  }

  const exitFullscreen = () => {
    if (document.exitFullscreen) {
      document.exitFullscreen()
      setIsFullscreen(false)
    }
  }

  const addToHomeScreen = () => {
    // This would trigger the PWA install prompt if available
    if (window.deferredPrompt) {
      window.deferredPrompt.prompt()
    } else {
      // Fallback: copy URL for manual bookmark
      navigator.clipboard.writeText(window.location.href)
      alert('Copy the URL to bookmark Editions Vibes on your home screen!')
    }
  }

  const enableVibrateNotifications = () => {
    if ('vibrate' in navigator) {
      navigator.vibrate([200, 100, 200]) // Demo vibration
      localStorage.setItem('editions-vibrate', 'true')
    }
  }

  return (
    <>
      {/* Mobile Status Bar */}
      <div className="sm:hidden bg-editions-dark-light/90 backdrop-blur-lg border-b border-white/10 px-4 py-2 flex items-center justify-between sticky top-0 z-40">
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 bg-editions-green rounded-full animate-pulse"></div>
          <span className="text-xs text-white font-medium">
            {formatTorontoTime(effectiveTime)} {getTorontoTimezone(effectiveTime)}
          </span>
          {isSimulating && (
            <span className="text-xs bg-editions-orange/20 text-editions-orange px-2 py-1 rounded-full">
              SIM
            </span>
          )}
        </div>
        
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowQuickActions(!showQuickActions)}
            className="p-1 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
          >
            <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zM12 13a1 1 0 110-2 1 1 0 010 2zM12 20a1 1 0 110-2 1 1 0 010 2z" />
            </svg>
          </button>
        </div>
      </div>

      {/* Quick Actions Panel */}
      {showQuickActions && (
        <div className="sm:hidden bg-editions-dark-light/95 backdrop-blur-lg border-b border-white/10 px-4 py-3 sticky top-12 z-30">
          <div className="flex flex-wrap gap-2">
            <button
              onClick={isFullscreen ? exitFullscreen : enterFullscreen}
              className="text-xs bg-white/10 hover:bg-white/20 text-white px-3 py-1 rounded-full 
                       transition-colors flex items-center gap-1"
            >
              {isFullscreen ? '🔲' : '⛶'} {isFullscreen ? 'Exit' : 'Fullscreen'}
            </button>
            
            <button
              onClick={addToHomeScreen}
              className="text-xs bg-white/10 hover:bg-white/20 text-white px-3 py-1 rounded-full 
                       transition-colors flex items-center gap-1"
            >
              📱 Add to Home
            </button>
            
            <button
              onClick={enableVibrateNotifications}
              className="text-xs bg-white/10 hover:bg-white/20 text-white px-3 py-1 rounded-full 
                       transition-colors flex items-center gap-1"
            >
              📳 Vibrate
            </button>
            
            <button
              onClick={() => {
                const text = `🚀 Live from Shopify Editions .DEV! ${currentSessions.length} sessions happening now, ${upcomingSessions.length} coming up next. #ShopifyEditions`
                navigator.share({ title: 'Editions Vibes', text, url: window.location.href })
              }}
              className="text-xs bg-editions-orange/20 hover:bg-editions-orange/30 text-editions-orange px-3 py-1 rounded-full 
                       transition-colors flex items-center gap-1"
            >
              📤 Quick Share
            </button>
          </div>
        </div>
      )}

      {/* Mobile Session Counter */}
      <div className="sm:hidden fixed bottom-4 right-4 z-50">
        <div className="bg-editions-orange/90 backdrop-blur-lg rounded-full px-3 py-2 shadow-lg">
          <div className="flex items-center gap-2 text-white text-xs font-medium">
            <div className="flex items-center gap-1">
              <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
              <span>{currentSessions.length} Live</span>
            </div>
            <div className="w-px h-3 bg-white/30"></div>
            <div className="flex items-center gap-1">
              <span>⏰</span>
              <span>{upcomingSessions.length} Next</span>
            </div>
          </div>
        </div>
      </div>

      {/* Landscape Mode Optimization */}
      {orientation === 'landscape' && (
        <div className="sm:hidden fixed top-2 left-2 z-40">
          <div className="bg-editions-dark-light/90 backdrop-blur-lg rounded-lg px-2 py-1 text-xs text-white">
            📱 Landscape mode optimized
          </div>
        </div>
      )}

      {/* PWA Install Prompt Styles */}
      <style jsx>{`
        @media (display-mode: standalone) {
          body {
            user-select: none;
            -webkit-user-select: none;
            -webkit-touch-callout: none;
          }
        }
        
        @media (orientation: landscape) and (max-height: 500px) {
          .mobile-optimize {
            padding-top: 0.5rem;
            padding-bottom: 0.5rem;
          }
        }
      `}</style>
    </>
  )
} 